/**
 * Форматирование сумм отчёта.
 *
 * Валюта продукта — рубль, и в контракте её нет: отчёт строится только по
 * рублёвым кабинетам. Константа стоит здесь одна на всю страницу — если
 * валют станет больше, она превратится в поле данных, и менять придётся
 * ровно это место.
 *
 * Хук, а не голые функции, чтобы вызывающий код не пересобирал форматтеры
 * на каждый рендер и чтобы переход на валюту из данных не потребовал
 * трогать восемь блоков.
 *
 * @format
 */

import {useMemo} from 'react';

import {billions, formatAmount, formatTarget, money, moneyParts} from './helpers';
import type {Amount, Target} from './types';

/** Валюта всех сумм отчёта. */
export const REPORT_CURRENCY = 'RUB';

export interface ReportFormat {
  /** `140932` → `140 932 ₽`. */
  money(value: number): string;
  /** Число и знак валюты по отдельности — для крупных цифр `Metric`. */
  moneyParts(value: number): {value: string; unit: string};
  /** Величина с единицей: «26 дней из 128», «43 %», «43 322 ₽». */
  amount(amount: Amount): string;
  /** Значение цели: «не более 3 300 ₽», «получен». */
  target(target: Target): string;
  /** Порядок величины: «32 млрд ₽». */
  billions(value: number): string;
}

export function useReportFormat(): ReportFormat {
  return useMemo(
    () => ({
      money: (value: number) => money(value, REPORT_CURRENCY),
      moneyParts: (value: number) => moneyParts(value, REPORT_CURRENCY),
      amount: (amount: Amount) => formatAmount(amount, REPORT_CURRENCY),
      target: (target: Target) => formatTarget(target, REPORT_CURRENCY),
      billions: (value: number) => billions(value, REPORT_CURRENCY),
    }),
    [],
  );
}
